import type { PositionAnchor } from "maplibre-gl";

import { STOP_LABEL_ABOVE_DOT_CONFIG, type StopLabelPlacementConfig } from "../stopLabels";
import type { ScreenRect, TransitPathStopCollisionEntry } from "./transitPathStopCollision.types";

/**
 * Returns whether two screen rectangles intersect (inclusive edges).
 *
 * @param a First rectangle.
 * @param b Second rectangle.
 * @returns True when the rectangles overlap.
 */
export function rectsOverlap( a: ScreenRect, b: ScreenRect ): boolean {
    const aRight = a.left + a.width;
    const aBottom = a.top + a.height;
    const bRight = b.left + b.width;
    const bBottom = b.top + b.height;

    return a.left < bRight && aRight > b.left && a.top < bBottom && aBottom > b.top;
}

/**
 * Expands a rectangle uniformly on all sides.
 *
 * @param rect Base rectangle.
 * @param padding Padding to add on each side (px).
 * @returns Inflated rectangle.
 */
export function inflateRect( rect: ScreenRect, padding: number ): ScreenRect {
    return {
        "left":   rect.left - padding,
        "top":    rect.top - padding,
        "width":  rect.width + padding * 2,
        "height": rect.height + padding * 2,
    };
}

/**
 * Computes the screen-space bounding box of a MapLibre HTML marker label.
 *
 * @param projectedX Projected anchor X in CSS pixels.
 * @param projectedY Projected anchor Y in CSS pixels.
 * @param width Label element width in pixels.
 * @param height Label element height in pixels.
 * @param anchor MapLibre position anchor.
 * @param offset Marker offset `[x, y]` in pixels.
 * @param padding Extra collision padding applied uniformly.
 * @returns Inflated screen rectangle for overlap testing.
 */
export function computeLabelScreenRect(
    projectedX: number,
    projectedY: number,
    width: number,
    height: number,
    anchor: PositionAnchor,
    offset: [number, number],
    padding: number,
): ScreenRect {
    const anchorX = projectedX + offset[0];
    const anchorY = projectedY + offset[1];

    let left = anchorX - width / 2;
    let top = anchorY - height / 2;

    switch ( anchor ) {
        case "top":
            left = anchorX - width / 2;
            top = anchorY;
            break;

        case "bottom":
            left = anchorX - width / 2;
            top = anchorY - height;
            break;

        case "left":
            left = anchorX - width;
            top = anchorY - height / 2;
            break;

        case "right":
            left = anchorX;
            top = anchorY - height / 2;
            break;

        case "top-left":
            left = anchorX;
            top = anchorY;
            break;

        case "top-right":
            left = anchorX - width;
            top = anchorY;
            break;

        case "bottom-left":
            left = anchorX;
            top = anchorY - height;
            break;

        case "bottom-right":
            left = anchorX - width;
            top = anchorY - height;
            break;

        case "center":
            left = anchorX - width / 2;
            top = anchorY - height / 2;
            break;

        default:
            left = anchorX - width / 2;
            top = anchorY - height / 2;
            break;
    }

    return inflateRect( {
        left,
        top,
        width,
        height,
    }, padding );
}

/**
 * Resolves the single placement config used for collision on intermediate stop labels.
 *
 * @param entry Stop label collision entry.
 * @returns Placement config (locked endpoint config or above-dot default).
 */
export function resolveStopLabelPlacementConfig( entry: Pick<TransitPathStopCollisionEntry, "labelConfig" | "lockPlacement"> ): StopLabelPlacementConfig {
    if ( entry.lockPlacement === true && entry.labelConfig !== undefined ) {
        return entry.labelConfig;
    }

    return entry.labelConfig ?? STOP_LABEL_ABOVE_DOT_CONFIG;
}

/**
 * Returns true when two placement configs are equivalent for marker positioning.
 *
 * @param a First config.
 * @param b Second config.
 * @returns Whether anchor and offset match.
 */
export function arePlacementConfigsEqual(
    a: StopLabelPlacementConfig,
    b: StopLabelPlacementConfig,
): boolean {
    return a.anchor === b.anchor &&
      a.offset[0] === b.offset[0] &&
      a.offset[1] === b.offset[1];
}

/**
 * Builds a stable fingerprint for collision entry sets to skip redundant marker remounts.
 *
 * @param entries Stop label entries about to be mounted.
 * @returns Fingerprint string compared across {@link TransitPathStopCollisionController#setEntries} calls.
 */
export function buildTransitPathStopEntriesFingerprint( entries: readonly TransitPathStopCollisionEntry[] ): string {
    return entries.map( ( entry ) => {
        const labelSignature = entry.labelElement?.textContent ?? "";
        const lng = entry.lngLat[0].toFixed( 6 );
        const lat = entry.lngLat[1].toFixed( 6 );

        return [
            entry.id,
            lng,
            lat,
            entry.priority,
            entry.lockPlacement === true ? "1" : "0",
            entry.dotElement !== undefined ? "dot" : "",
            labelSignature,
        ].join( "|" );
    } ).join( ";" );
}
